// src/pages/portal/FilemakerSyncCard.tsx
//
// Customer-facing FileMaker sync panel.
//
// Lives under /portal (the customer's view). Shows only the
// three layouts the customer actually cares about:
//   - their SALES Order writeback status
//   - their QUOTATIONS writeback status
//   - their REPAIR ticket status
//
// Per docs/proposals/filemaker-sync.md \$3.8 (Leak #7):
// the BENCHMARK portal cannot edit customer metadata - this
// card is read-only.

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Database,
  Check,
  RefreshCcw,
  Shield,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";
import { useSyncState } from "@/lib/filemaker-sync/useSyncState";
import {
  FILEMAKER_LAYOUT_BY_KEY,
  type FilemakerLayoutKey,
} from "@/lib/filemaker-sync/types";

const CUSTOMER_VISIBLE_LAYOUTS: FilemakerLayoutKey[] = [
  "sales-orders",
  "quotations",
  "repairs",
];

export function FilemakerSyncCard() {
  const { t } = useTranslation();
  const state = useSyncState();

  const relTime = (iso: string): string => {
    const ms = Date.now() - new Date(iso).getTime();
    const s = Math.round(ms / 1000);
    if (s < 60) return t("portal.filemakerSyncCard.secondsAgo", { n: s });
    if (s < 3600) return t("portal.filemakerSyncCard.minutesAgo", { n: Math.round(s / 60) });
    if (s < 86400) return t("portal.filemakerSyncCard.hoursAgo", { n: Math.round(s / 3600) });
    return t("portal.filemakerSyncCard.daysAgo", { n: Math.round(s / 86400) });
  };
  const rows = useMemo(
    () =>
      CUSTOMER_VISIBLE_LAYOUTS.map((k) => {
        const cfg = FILEMAKER_LAYOUT_BY_KEY[k];
        const status = state.layouts[k];
        return { cfg, status };
      }),
    [state],
  );
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Database
              className="h-4 w-4 text-benchmark-teal-bright"
              strokeWidth={1.5}
            />
            BENCHMARK ↔ FileMaker
          </CardTitle>
          <CardDescription>
            {t("portal.filemakerSyncCard.description")}
          </CardDescription>
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-fg-quaternary">
          <Shield className="h-3 w-3" strokeWidth={1.5} />
          {t("portal.filemakerSyncCard.readOnly")}
        </span>
      </CardHeader>
      <CardContent className="space-y-2 p-4 pt-0">
        {rows.map(({ cfg, status }) => (
          <div
            key={cfg.key}
            className="flex items-center justify-between gap-2 rounded-md border border-border bg-bg-canvas/40 p-2"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-510 text-fg-primary">
                  {cfg.label}
                </span>
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-510 uppercase tracking-wider",
                    status.health === "ok"
                      ? "bg-accent-green/15 text-accent-green"
                      : status.health === "warning"
                        ? "bg-accent-amber/15 text-accent-amber"
                        : "bg-accent-rose/15 text-accent-rose",
                  )}
                >
                  {status.health === "ok" ? (
                    <Check className="h-2.5 w-2.5" strokeWidth={2} />
                  ) : (
                    <RefreshCcw className="h-2.5 w-2.5" strokeWidth={2} />
                  )}
                  {status.health === "ok"
                    ? t("portal.filemakerSyncCard.synced")
                    : t(
                        status.health === "warning"
                          ? "portal.filemakerSyncCard.warning"
                          : "portal.filemakerSyncCard.error",
                      )}
                </span>
              </div>
              <div className="mt-0.5 truncate text-[10px] text-fg-tertiary">
                {t("portal.filemakerSyncCard.lastWriteback")} {relTime(status.lastSyncedAt)} ·{" "}
                {status.totalRows.toLocaleString()} {t("portal.filemakerSyncCard.rowsMirrored")}
              </div>
            </div>
            {status.openAnomalies.length > 0 ? (
              <Badge variant="default" size="sm">
                {t("portal.filemakerSyncCard.openCount", { n: status.openAnomalies.length })}
              </Badge>
            ) : (
              <Link
                to={
                  cfg.key === "repairs"
                    ? "/portal/repairs"
                    : cfg.key === "sales-orders"
                      ? "/portal/orders"
                      : cfg.key === "quotations"
                        ? "/portal/orders"
                        : "/portal"
                }
                className="text-[10px] text-fg-tertiary hover:text-fg-primary"
              >
                {t("portal.filemakerSyncCard.view")}
              </Link>
            )}
          </div>
        ))}
        <p className="border-t border-border pt-2 text-[10px] text-fg-quaternary">
          {t("portal.filemakerSyncCard.masterDataNote")}
        </p>
      </CardContent>
    </Card>
  );
}
